// utils/yearComparison.ts
// Pure logic for the "Jämför år" tab: the span each year covers, the totals per
// year and the cumulative chart series. Dates are 'YYYY-MM-DD' strings; all
// arithmetic is done in UTC so the local time zone never shifts a day.
import { DateRange, ServiceLogEntry, ServiceType, TimeGrouping } from '../types/analysis.types';
import { extractIndividualVisits } from './dataProcessing';
import { MONTH_NAMES } from './constants';
import { getISOWeekNumber } from './dateUtils';

/** A day in the year without the year: month 1–12, day 1–31 */
export interface DayMonth {
    month: number;
    day: number;
}

export type ComparisonGrouping = TimeGrouping;

/** 'visits' or a service type key */
export type ComparisonMetric = string;

export const VISITS_METRIC: ComparisonMetric = 'visits';

const DAY_MS = 86400000;
const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

export const parseDayMonth = (date: string): DayMonth | null => {
    const match = DATE_RE.exec(date);
    if (!match) return null;
    const month = Number(match[2]);
    const day = Number(match[3]);
    if (month < 1 || month > 12 || day < 1 || day > 31) return null;
    return { month, day };
};

const compareDayMonth = (a: DayMonth, b: DayMonth): number =>
    a.month !== b.month ? a.month - b.month : a.day - b.day;

const daysInMonth = (year: number, month: number): number =>
    new Date(Date.UTC(year, month, 0)).getUTCDate();

/** The day-month in a given year; 29 feb becomes 28 feb in a non-leap year */
const toDate = (year: number, dayMonth: DayMonth): string => {
    const day = Math.min(dayMonth.day, daysInMonth(year, dayMonth.month));
    return `${year}-${String(dayMonth.month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
};

const toUtc = (date: string): number => {
    const [year, month, day] = date.split('-').map(Number);
    return Date.UTC(year, month - 1, day);
};

/**
 * The span of comparison year `year`: Från's day-month in that year up to
 * Till's day-month, which falls in the next year when it comes before Från
 * (a span across New Year is labelled by Från's year).
 */
export const yearSpan = (year: number, from: DayMonth, to: DayMonth): DateRange => {
    const endYear = compareDayMonth(to, from) < 0 ? year + 1 : year;
    return { start: toDate(year, from), end: toDate(endYear, to) };
};

/** The comparison year whose span contains `today`, or null when today is outside every span */
export const seasonYearOf = (today: string, from: DayMonth, to: DayMonth): number | null => {
    const year = Number(today.slice(0, 4));
    for (const candidate of [year, year - 1]) {
        const span = yearSpan(candidate, from, to);
        if (span.start <= today && today <= span.end) return candidate;
    }
    return null;
};

/**
 * The same-date cut-off: every year's span ends at the point the current
 * season has reached today. Null when today is in no season, or on its last day
 * (nothing left to cut).
 */
export const sameDateCutOff = (
    today: string,
    from: DayMonth,
    to: DayMonth,
): { seasonYear: number; offsetDays: number } | null => {
    const seasonYear = seasonYearOf(today, from, to);
    if (seasonYear === null) return null;
    const span = yearSpan(seasonYear, from, to);
    if (today >= span.end) return null;
    return { seasonYear, offsetDays: Math.round((toUtc(today) - toUtc(span.start)) / DAY_MS) };
};

/** The year's span ended `offsetDays` after its start (or at its own end, whichever comes first) */
export const cutSpan = (span: DateRange, offsetDays: number): DateRange => {
    const cut = new Date(toUtc(span.start) + offsetDays * DAY_MS).toISOString().slice(0, 10);
    return { start: span.start, end: cut < span.end ? cut : span.end };
};

/** What one log adds to a service's total: its value when the service collects one, else 1 (as the top list counts) */
const serviceContribution = (entry: ServiceLogEntry, serviceType: ServiceType | undefined): number =>
    serviceType?.collectValue ? (entry.value ?? 0) : 1;

export interface YearTotals {
    /** Building visits: each building counted once per day */
    visits: number;
    /** Buildings with at least one log */
    buildingsWithVisits: number;
    /** Per service type key */
    byService: Record<string, number>;
}

export const yearTotals = (entries: ServiceLogEntry[], serviceTypes: ServiceType[]): YearTotals => {
    const byService: Record<string, number> = {};
    serviceTypes.forEach(type => {
        if (type.key) byService[type.key] = 0;
    });
    entries.forEach(entry => {
        if (!entry.serviceType || !(entry.serviceType in byService)) return;
        const type = serviceTypes.find(t => t.key === entry.serviceType);
        byService[entry.serviceType] += serviceContribution(entry, type);
    });
    return {
        visits: extractIndividualVisits(entries).length,
        buildingsWithVisits: new Set(entries.map(entry => entry.buildingKey)).size,
        byService,
    };
};

/** % change from the previous year; null when there is nothing to compare with */
export const changePct = (previous: number, current: number): number | null =>
    previous === 0 ? null : ((current - previous) / previous) * 100;

/** '+12 %', '−5 %', '±0 %' */
export const formatChange = (pct: number): string => {
    const rounded = Math.round(pct);
    if (rounded === 0) return '±0 %';
    return rounded > 0 ? `+${rounded} %` : `−${Math.abs(rounded)} %`;
};

/** Monday of the ISO week containing the date, as a UTC timestamp */
const mondayOf = (date: string): number => {
    const utc = toUtc(date);
    const weekday = (new Date(utc).getUTCDay() + 6) % 7;
    return utc - weekday * DAY_MS;
};

/** Day of the year on a leap-year calendar (1 jan = 0, 29 feb = 59, 1 mar = 60 in every year) */
const leapDayOfYear = (date: string): number => {
    const [, month, day] = date.split('-').map(Number);
    return Math.round((Date.UTC(2000, month - 1, day) - Date.UTC(2000, 0, 1)) / DAY_MS);
};

const LEAP_YEAR_DAYS = 366;

/**
 * The period index of a date within its span. Years are aligned on this
 * index, so period 0 is the first day/week/month of the span in every year:
 * - days: on the date, over a leap-year calendar, so 15 jun lines up with
 *   15 jun in every year; 29 feb has its own slot that only leap years fill
 * - weeks: Monday-based (as ISO weeks) since the span's start
 * - months: calendar months since the span's start
 */
export const periodIndex = (date: string, spanStart: string, grouping: ComparisonGrouping): number => {
    if (grouping === 'daily') {
        const years = Number(date.slice(0, 4)) - Number(spanStart.slice(0, 4));
        return years * LEAP_YEAR_DAYS + leapDayOfYear(date) - leapDayOfYear(spanStart);
    }
    if (grouping === 'monthly') {
        const [y, m] = date.split('-').map(Number);
        const [sy, sm] = spanStart.split('-').map(Number);
        return (y - sy) * 12 + (m - sm);
    }
    return Math.round((mondayOf(date) - mondayOf(spanStart)) / (7 * DAY_MS));
};

/** The label of a period, taken from the reference year's span ('15 jun', 'v. 23', 'Jun') */
export const periodLabel = (index: number, referenceSpanStart: string, grouping: ComparisonGrouping): string => {
    if (grouping === 'daily') {
        const dayOfYear = (leapDayOfYear(referenceSpanStart) + index) % LEAP_YEAR_DAYS;
        const date = new Date(Date.UTC(2000, 0, 1) + dayOfYear * DAY_MS);
        return `${date.getUTCDate()} ${MONTH_NAMES[date.getUTCMonth()].toLowerCase()}`;
    }
    if (grouping === 'monthly') {
        const startMonth = Number(referenceSpanStart.slice(5, 7)) - 1;
        return MONTH_NAMES[(startMonth + index) % 12];
    }
    const monday = new Date(mondayOf(referenceSpanStart) + index * 7 * DAY_MS);
    // getISOWeekNumber reads local date parts; noon UTC keeps the same calendar day in Sweden
    const local = new Date(monday.getUTCFullYear(), monday.getUTCMonth(), monday.getUTCDate(), 12);
    return `v. ${String(getISOWeekNumber(local)).padStart(2, '0')}`;
};

export interface YearSeriesInput {
    year: number;
    span: DateRange;
    entries: ServiceLogEntry[];
    /** Last date this year's line reaches (today for the current season); absent = the whole span */
    lastDate?: string;
}

export interface ChartRow {
    period: string;
    [year: string]: string | number | undefined;
}

/** What each entry adds to the metric on its date; visits are deduplicated per building and day first */
const metricPoints = (
    entries: ServiceLogEntry[],
    metric: ComparisonMetric,
    serviceTypes: ServiceType[],
): Array<{ date: string; amount: number }> => {
    if (metric === VISITS_METRIC) {
        return extractIndividualVisits(entries).map(entry => ({ date: entry.date, amount: 1 }));
    }
    const type = serviceTypes.find(t => t.key === metric);
    return entries
        .filter(entry => entry.serviceType === metric)
        .map(entry => ({ date: entry.date, amount: serviceContribution(entry, type) }));
};

/**
 * One row per period with each year's running total. A year's line stops
 * (undefined) after its `lastDate`. Labels come from the last year in `years`.
 */
export const cumulativeSeries = (
    years: YearSeriesInput[],
    metric: ComparisonMetric,
    serviceTypes: ServiceType[],
    grouping: ComparisonGrouping,
): ChartRow[] => {
    if (years.length === 0) return [];
    const periodCount = Math.max(
        ...years.map(year => periodIndex(year.span.end, year.span.start, grouping) + 1),
    );
    const reference = years[years.length - 1];
    const rows: ChartRow[] = Array.from({ length: periodCount }, (_, index) => ({
        period: periodLabel(index, reference.span.start, grouping),
    }));

    years.forEach(year => {
        const perPeriod = new Array<number>(periodCount).fill(0);
        metricPoints(year.entries, metric, serviceTypes).forEach(point => {
            const index = periodIndex(point.date, year.span.start, grouping);
            if (index >= 0 && index < periodCount) perPeriod[index] += point.amount;
        });
        const lastIndex = periodIndex(year.lastDate ?? year.span.end, year.span.start, grouping);
        let running = 0;
        perPeriod.forEach((amount, index) => {
            running += amount;
            rows[index][String(year.year)] = index <= lastIndex ? running : undefined;
        });
    });

    return rows;
};

/** How many years back the År picker offers */
export const YEARS_BACK = 5;

/**
 * The years the År picker offers, oldest first: the current year always, and
 * each of the YEARS_BACK years before it that has logs.
 */
export const comparisonYearChoices = (currentYear: number, yearsWithData: number[]): number[] => {
    const withData = new Set(yearsWithData);
    const earlier = Array.from({ length: YEARS_BACK }, (_, i) => currentYear - YEARS_BACK + i)
        .filter(year => withData.has(year));
    return [...earlier, currentYear];
};

/** This year and last year, as far as they are offered */
export const defaultComparisonYears = (choices: number[], currentYear: number): number[] =>
    [currentYear - 1, currentYear].filter(year => choices.includes(year));

/** Years from the URL ('2025,2026'), kept only when they are offered; sorted, oldest first */
export const parseYearsParam = (param: string | null, availableYears: number[]): number[] => {
    if (!param) return [];
    const available = new Set(availableYears);
    const years = param
        .split(',')
        .map(Number)
        .filter(year => Number.isInteger(year) && available.has(year));
    return Array.from(new Set(years)).sort((a, b) => a - b);
};

/** The years under `servicesLogs`, oldest first */
export const yearsWithLogs = (servicesLogs: Record<string, unknown> | undefined): number[] =>
    Object.keys(servicesLogs ?? {})
        .filter(key => /^\d{4}$/.test(key))
        .map(Number)
        .sort((a, b) => a - b);

/** The day labels that start a month ('1 jan', '1 feb' …): the x-axis ticks for days */
export const monthStartTicks = (rows: ChartRow[]): string[] =>
    rows.map(row => row.period).filter(period => /^1 /.test(period));
